import { createContext, useCallback, useContext, useState } from "react";
import { postAuthenticatedRequest } from "../../utils/api/requests/request";
import { message } from "antd";

const UserPreferenceContext = createContext();

export const UserPreferenceProvider = ({
  children,
  initialPreferences = {},
}) => {
  const [userPreferences, setUserPreferences] = useState(initialPreferences);

  const getUserPreference = async (key) => {
    const response = await postAuthenticatedRequest(
      "RetrieveUserPreferenceQuery",
      { key },
    );
    if (response?.outData?.isSuccessful) {
      const data = response?.outData?.data || {};
      // Merge new preference data with existing preferences
      setUserPreferences((prev) => ({ ...prev, ...data }));
    } else {
      message.error(response?.outData?.message);
    }
  };

  const getUserPreferences = async (keys = []) => {
    try {
      if (!Array.isArray(keys) || keys.length === 0) return {};

      // Fetch all preferences in parallel for better performance
      const results = await Promise.all(
        keys.map((k) =>
          postAuthenticatedRequest("RetrieveUserPreferenceQuery", { key: k }),
        ),
      );

      // Merge successful responses into a single preferences object
      const merged = results.reduce((acc, resp) => {
        if (resp?.outData?.isSuccessful && resp?.outData?.data) {
          Object.assign(acc, resp.outData.data);
        }
        return acc;
      }, {});

      // Update local state with fetched preferences
      setUserPreferences((prev) => ({ ...prev, ...merged }));

      return merged;
    } catch (err) {
      // Surface a single error message, but avoid breaking UI
      // message.error("Failed to retrieve user preferences");
      return {};
    }
  };

  const updateUserPreference = useCallback(async (key, value) => {
    const data = { preferences: { [key]: value } };
    const response = await postAuthenticatedRequest(
      "UpdateUserPreferencesCommand",
      data,
    );
    if (response?.outData?.isSuccessful) {
      // The value we submitted is authoritative. Updating it locally avoids a
      // second request and a full provider re-render immediately after every
      // preference change (for example, dashboard-style switching).
      setUserPreferences((prev) => ({ ...prev, [key]: value }));
    } else {
      message.error(response?.outData?.message);
    }
    return response;
  }, []);

  return (
    <UserPreferenceContext.Provider
      value={{
        userPreferences,
        updateUserPreference,
        getUserPreference,
        getUserPreferences,
      }}
    >
      {children}
    </UserPreferenceContext.Provider>
  );
};

export const useUserPreference = () => useContext(UserPreferenceContext);
